/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { CalendarDays, Ellipsis, House, LucideIcon, MapPinned, ShoppingCart } from "lucide-react";

import { TestIds } from "../../../testIds";

import { NavDestination, NavDestinationId, useNavDestinations } from "./useNavDestinations";

type BottomNavItemConfig = {
  destinationId: NavDestinationId;
  label: string;
  icon: LucideIcon;
  testId?: string;
};

/**
 * Bottom nav's own presentation config — order, label, and icon are all decided
 * here and only here, independent of the side nav's config.
 */
const BOTTOM_NAV_CONFIG: BottomNavItemConfig[] = [
  {
    destinationId: "home",
    label: "Home",
    icon: House,
    testId: TestIds.components.bottomDrawer.home,
  },
  {
    destinationId: "shop",
    label: "Shop",
    icon: ShoppingCart,
    testId: TestIds.components.bottomDrawer.pastProducts,
  },
  {
    destinationId: "agreements",
    label: "Agreements",
    icon: CalendarDays,
    testId: TestIds.components.bottomDrawer.agreements,
  },
  {
    destinationId: "cardPartners",
    label: "Partners",
    icon: MapPinned,
    testId: TestIds.components.bottomDrawer.cardPartners,
  },
  {
    destinationId: "account",
    label: "More",
    icon: Ellipsis,
    testId: TestIds.components.bottomDrawer.account,
  },
];

/** The destination IDs that make up the mobile bottom-nav base tabs. */
export const BOTTOM_NAV_DESTINATION_IDS: NavDestinationId[] = BOTTOM_NAV_CONFIG.map(
  (item) => item.destinationId
);

export type BottomNavItem = BottomNavItemConfig & NavDestination;

export const useBottomNavItems = (): BottomNavItem[] => {
  const destinations = useNavDestinations();

  return BOTTOM_NAV_CONFIG.filter((item) => destinations[item.destinationId].visible).map(
    (item) => ({
      ...item,
      ...destinations[item.destinationId],
    })
  );
};
